import { useState, useEffect, useMemo } from 'react'
import { X, Download, Copy, Loader2, Play } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import ClipPreview from './ClipPreview'

function fmtTime(ms) {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function vttTime(ms) {
  const totalSec = ms / 1000
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${s.toFixed(3).padStart(6, '0')}`
}

function buildPlainText(cues) {
  return cues.map(c => `[${fmtTime(c.start_ms)}] ${c.text}`).join('\n')
}

function buildVtt(cues) {
  const blocks = cues.map((c, i) => {
    return `${i + 1}\n${vttTime(c.start_ms)} --> ${vttTime(c.end_ms)}\n${c.text}`
  })
  return `WEBVTT\n\n${blocks.join('\n\n')}\n`
}

function download(filename, body, mime) {
  const blob = new Blob([body], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { URL.revokeObjectURL(url); a.remove() }, 0)
}

export default function TranscriptViewer({ tbtv_id, language = 'en', onClose }) {
  const api = useHighlightsApi()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [filter, setFilter] = useState('')
  const [previewMs, setPreviewMs] = useState(null) // ms when an inner clip preview is open
  const [chapters, setChapters] = useState([])     // Mux Robots: Generate Chapters

  useEffect(() => {
    let cancelled = false
    api.transcript(tbtv_id, { language })
      .then(r => { if (!cancelled) setData(r) })
      .catch(e => { if (!cancelled) setErr(e.message) })
    api.chapters(tbtv_id)
      .then(r => { if (!cancelled) setChapters(r?.chapters || []) })
      .catch(() => { /* chapters are optional; ignore if missing */ })
    return () => { cancelled = true }
  }, [tbtv_id, language])

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const filtered = useMemo(() => {
    if (!data?.cues) return []
    if (!filter.trim()) return data.cues
    const needle = filter.toLowerCase()
    return data.cues.filter(c => c.text.toLowerCase().includes(needle))
  }, [data, filter])

  const safeName = (data?.title || tbtv_id).replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80)

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div style={{
        background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(900px, 96vw)', height: 'min(85vh, 720px)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {data?.title || tbtv_id}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>
              {tbtv_id} · {language}
              {data && ` · ${data.cue_count?.toLocaleString()} cues · ${data.word_count?.toLocaleString()} words`}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Filter cues by text…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: '6px 10px', background: '#000', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          />
          <button
            onClick={() => navigator.clipboard?.writeText(buildPlainText(data?.cues || []))}
            disabled={!data}
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Copy size={12} /> Copy text
          </button>
          <button
            onClick={() => download(`${safeName}.txt`, buildPlainText(data?.cues || []), 'text/plain')}
            disabled={!data}
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Download size={12} /> .txt
          </button>
          <button
            onClick={() => download(`${safeName}.vtt`, buildVtt(data?.cues || []), 'text/vtt')}
            disabled={!data}
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Download size={12} /> .vtt
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Chapters sidebar (Mux Robots) */}
          {chapters.length > 0 && (
            <div style={{ width: 220, borderRight: '1px solid #1f1f1f', overflowY: 'auto', padding: '8px 0' }}>
              <div style={{ padding: '4px 12px', fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Chapters · {chapters.length}
              </div>
              {chapters.map((ch, i) => (
                <button
                  key={`ch-${ch.ord}-${i}`}
                  onClick={() => {
                    setFilter('')
                    setTimeout(() => {
                      const el = document.getElementById(`cue-${ch.start_ms}`)
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      else {
                        // Fallback: find first cue at or after this start_ms
                        const target = (data?.cues || []).find(c => c.start_ms >= ch.start_ms)
                        if (target) document.getElementById(`cue-${target.start_ms}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    }, 30)
                  }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 12px', background: 'transparent', border: 'none',
                    cursor: 'pointer', color: '#ddd', fontSize: 12, lineHeight: 1.35,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#1a1a1a' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                >
                  <div style={{ color: '#666', fontFamily: 'monospace', fontSize: 11 }}>{fmtTime(ch.start_ms)}</div>
                  <div>{ch.title}</div>
                </button>
              ))}
            </div>
          )}

          {/* Cue list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
            {!data && !err && (
              <div style={{ padding: 24, color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Loader2 size={14} className="spin" /> Loading transcript…
              </div>
            )}
            {err && <div style={{ color: 'crimson', padding: 12 }}>{err}</div>}
            {data && filtered.length === 0 && (
              <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>
                {filter ? 'No cues match that filter.' : 'No cues (this should not happen for an indexed transcript).'}
              </div>
            )}
            {data && filtered.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 70px 1fr', columnGap: 12, rowGap: 4, fontSize: 13, lineHeight: 1.5, alignItems: 'center' }}>
                {filtered.map((c, i) => (
                  <Cue key={`${c.start_ms}-${i}`} cue={c} highlight={filter} onPlay={() => setPreviewMs(c.start_ms)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid #1f1f1f', fontSize: 11, color: '#666' }}>
          {data && filter && `Showing ${filtered.length.toLocaleString()} of ${data.cue_count?.toLocaleString()} cues`}
          {data && !filter && `${data.cue_count?.toLocaleString()} cues · indexed ${data.indexed_at}`}
        </div>
      </div>

      {previewMs != null && (
        <ClipPreview
          tbtv_id={tbtv_id}
          startMs={previewMs}
          title={data?.title}
          onClose={() => setPreviewMs(null)}
        />
      )}
    </div>
  )
}

function Cue({ cue, highlight, onPlay }) {
  // Highlight matched substring if there's an active filter.
  let text = cue.text
  let inner = text
  if (highlight && highlight.trim()) {
    try {
      const re = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig')
      const parts = text.split(re)
      inner = parts.map((p, i) =>
        re.test(p) ? <mark key={i} style={{ background: '#3b82f6', color: 'white', padding: '0 2px' }}>{p}</mark> : p
      )
    } catch { /* fall through */ }
  }
  return (
    <>
      <button onClick={onPlay} title="Preview ±10s"
        style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', padding: '3px 6px', display: 'inline-flex', alignItems: 'center', fontSize: 11 }}>
        <Play size={11} />
      </button>
      <div id={`cue-${cue.start_ms}`} style={{ color: '#666', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', scrollMarginTop: 8 }}>{fmtTime(cue.start_ms)}</div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{inner}</div>
    </>
  )
}
